import debounce from 'lodash.debounce';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '~/components/shadcn/button-simple';
import { CameraCapture } from '~/components/shadcn/camera-capture-simple';
import { Card } from '~/components/shadcn/card-simple';
import { Combobox, type ComboboxOption } from '~/components/shadcn/combobox-simple';
import { Input } from '~/components/shadcn/input-simple';
import { Label } from '~/components/shadcn/label-simple';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/shadcn/table-simple';
import api from '~/services/api';
import history from '~/services/history';

import type {
  InventoryItemLookupResponse,
  ItemSearchResult,
  OrderChecklistItem,
  ReceivingPayload,
  ReceivingPayloadItem,
  RmaDetail,
  RmaReceivingRow,
} from './types';

const CARRIER_OPTIONS: ComboboxOption[] = [
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'purolator', label: 'Purolator' },
  { value: 'dhl', label: 'DHL' },
  { value: 'usps', label: 'USPS' },
  { value: 'other', label: 'Other' },
];

function parseSerialList(value?: string) {
  if (!value) return [];

  return value
    .split(',')
    .map((serial) => serial.trim().toUpperCase())
    .filter(Boolean);
}

function normalizeCarrier(rawCarrier?: string) {
  const normalizedCarrier = rawCarrier?.trim().toLowerCase() || '';
  const existing = CARRIER_OPTIONS.find((option) => option.value === normalizedCarrier || option.label.toLowerCase() === normalizedCarrier);
  return existing?.value || '';
}

async function fetchItemBySerialNumber(serialNumber: string) {
  const response = await api.post<InventoryItemLookupResponse>('inventory_items/showBySerialNumber', {
    serial_number: serialNumber,
  });

  return response.data;
}

async function fetchItemsBySearch(search: string) {
  const response = await api.get<ItemSearchResult[]>(`items?search=${encodeURIComponent(search)}`);
  return response.data || [];
}

export function NewReceivingRma() {
  const { returnId } = useParams<{ returnId: string }>();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rma, setRma] = useState<RmaDetail | null>(null);
  const [trackingCode, setTrackingCode] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [notes, setNotes] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [evidences, setEvidences] = useState<File[]>([]);
  const [receivingRows, setReceivingRows] = useState<RmaReceivingRow[]>([]);

  const [orderItemsModalOpen, setOrderItemsModalOpen] = useState(false);
  const [orderItemsLoading, setOrderItemsLoading] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderChecklistItem[]>([]);
  const [selectedOrderItemIds, setSelectedOrderItemIds] = useState<Set<number>>(new Set());

  const [extraSerialInput, setExtraSerialInput] = useState('');
  const [fallbackSerial, setFallbackSerial] = useState('');
  const [manualAdditionalItemSearch, setManualAdditionalItemSearch] = useState('');
  const [manualAdditionalItemLoading, setManualAdditionalItemLoading] = useState(false);
  const [manualAdditionalItemOptions, setManualAdditionalItemOptions] = useState<ItemSearchResult[]>([]);
  const [manualAdditionalSelectedItem, setManualAdditionalSelectedItem] = useState<ItemSearchResult | null>(null);
  const [additionalItems, setAdditionalItems] = useState<
    Array<{ quantity: number; serial_number: string; item_id: number; part_number?: string; description?: string }>
  >([]);

  const hasReceivedItems = useMemo(() => receivingRows.some((row) => row.received), [receivingRows]);
  const hasAdditionalItems = useMemo(() => additionalItems.some((item) => item.serial_number.trim() && item.item_id > 0), [additionalItems]);

  const debouncedFetchItems = useMemo(
    () =>
      debounce(async (searchText: string) => {
        if (!searchText.trim()) {
          setManualAdditionalItemOptions([]);
          setManualAdditionalItemLoading(false);
          return;
        }

        try {
          const data = await fetchItemsBySearch(searchText);
          setManualAdditionalItemOptions(data);
        } catch {
          setManualAdditionalItemOptions([]);
        } finally {
          setManualAdditionalItemLoading(false);
        }
      }, 500),
    []
  );

  function resetManualAdditionalItemFields() {
    setManualAdditionalItemSearch('');
    setManualAdditionalItemLoading(false);
    setManualAdditionalItemOptions([]);
    setManualAdditionalSelectedItem(null);
  }

  function handleManualItemSearchChange(searchText: string) {
    setManualAdditionalItemSearch(searchText);
    setManualAdditionalSelectedItem(null);

    if (!searchText.trim()) {
      setManualAdditionalItemOptions([]);
      setManualAdditionalItemLoading(false);
      debouncedFetchItems.cancel();
      return;
    }

    setManualAdditionalItemLoading(true);
    debouncedFetchItems(searchText);
  }

  function handleSelectManualItem(item: ItemSearchResult) {
    setManualAdditionalSelectedItem(item);
    setManualAdditionalItemSearch(`${item.name} | ${item.description || '-'}`);
    setManualAdditionalItemOptions([]);
    setManualAdditionalItemLoading(false);
  }

  const loadRowsFromSerials = useCallback(async (serials: string[]) => {
    const results = await Promise.allSettled(serials.map((serialNumber) => fetchItemBySerialNumber(serialNumber)));

    const mappedRows: RmaReceivingRow[] = results.map((result, index) => {
      const serialNumber = serials[index];

      if (result.status !== 'fulfilled') {
        return {
          id: `base-${serialNumber}-${index + 1}`,
          serialNumber,
          itemName: 'Not found',
          itemDescription: 'Not found',
          partNumber: '-',
          brand: '-',
          received: false,
          isExtra: false,
          lookupFound: false,
        };
      }

      const data = result.value;
      const lookupFound = !data.error && Boolean(data.item?.id);

      return {
        id: `base-${serialNumber}-${index + 1}`,
        serialNumber,
        itemName: lookupFound ? data.item?.name || '-' : 'Not found',
        itemDescription: lookupFound ? data.item?.description || '-' : 'Not found',
        partNumber: lookupFound ? data.item?.part_number || '-' : '-',
        brand: lookupFound ? data.item?.brand?.name || '-' : '-',
        received: lookupFound,
        isExtra: false,
        lookupFound,
      };
    });

    setReceivingRows(mappedRows);
  }, []);

  async function loadOrderItems() {
    if (!rma) return;

    setOrderItemsLoading(true);
    try {
      const { data } = await api.get<OrderChecklistItem[]>(`ordersChecklist/${rma.order_id}`);
      const selectedSerials = new Set(
        receivingRows
          .filter((row) => row.received)
          .map((row) => row.serialNumber.trim().toUpperCase())
          .filter(Boolean)
      );

      const preselectedItemIds = data.filter((item) => selectedSerials.has((item.serial_number || '').trim().toUpperCase())).map((item) => item.id);

      setOrderItems(data);
      setSelectedOrderItemIds(new Set(preselectedItemIds));
      setOrderItemsModalOpen(true);
    } catch {
      toast.error('Could not load order items.');
    } finally {
      setOrderItemsLoading(false);
    }
  }

  function handleOrderItemSelect(itemId: number, isSelected: boolean) {
    const item = orderItems.find((orderItem) => orderItem.id === itemId);
    const hasSerialNumber = Boolean(item?.serial_number?.trim());
    if (!hasSerialNumber) {
      return;
    }

    const newSelected = new Set(selectedOrderItemIds);
    if (isSelected) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedOrderItemIds(newSelected);
  }

  function handleCloseOrderItemsModal() {
    setOrderItemsModalOpen(false);
    setSelectedOrderItemIds(new Set());
  }

  function handleApplyOrderItems() {
    const selectedItems = orderItems.filter((item) => selectedOrderItemIds.has(item.id));
    const newRows: RmaReceivingRow[] = selectedItems.map((item, index) => ({
      id: `order-${item.serial_number}-${index + 1}`,
      serialNumber: item.serial_number,
      itemName: item.item?.name || '-',
      itemDescription: item.item?.description || '-',
      partNumber: item.item?.part_number || '-',
      brand: item.item?.brand?.name || '-',
      received: true,
      isExtra: false,
      lookupFound: true,
    }));

    setReceivingRows(newRows);
    handleCloseOrderItemsModal();
    toast.success(`${newRows.length} item(s) loaded from order.`);
  }

  useEffect(() => () => debouncedFetchItems.cancel(), [debouncedFetchItems]);

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && orderItemsModalOpen) {
        handleCloseOrderItemsModal();
      }
    }

    window.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [orderItemsModalOpen]);

  useEffect(() => {
    if (!returnId) return;

    async function loadRma() {
      setLoading(true);
      try {
        const { data } = await api.get<RmaDetail>(`/returns/${returnId}`);
        setRma(data);
        setTrackingCode(data.tracking || '');
        setCarrierId(normalizeCarrier(data.carrier));

        const serials = parseSerialList(data.items_returned);
        await loadRowsFromSerials(serials);
      } catch {
        toast.error('Could not load RMA data.');
      } finally {
        setLoading(false);
      }
    }

    loadRma();
  }, [loadRowsFromSerials, returnId]);

  function handleEvidenceAdd(event: ChangeEvent<HTMLInputElement>) {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
    if (!selectedFiles.length) return;

    setEvidences((current) => [...current, ...selectedFiles]);
    event.target.value = '';
  }

  function setReceivedState(rowId: string, received: boolean) {
    setReceivingRows((current) => current.map((row) => (row.id === rowId ? { ...row, received } : row)));
  }

  async function handleAddExtraItem() {
    const normalizedSerial = extraSerialInput.trim().toUpperCase();
    if (!normalizedSerial) {
      toast.error('Enter a serial number first.');
      return;
    }

    const duplicate = receivingRows.some((row) => row.serialNumber.toUpperCase() === normalizedSerial);
    if (duplicate) {
      toast.error('This serial number is already in the list.');
      return;
    }

    try {
      const data = await fetchItemBySerialNumber(normalizedSerial);

      if (data.error || !data.item?.id) {
        setFallbackSerial(normalizedSerial);
        resetManualAdditionalItemFields();
        toast.error('Serial not found. Search and select an item below to include it as a manual additional item.');
        return;
      }

      setReceivingRows((current) => [
        ...current,
        {
          id: `extra-${normalizedSerial}-${Date.now()}`,
          serialNumber: normalizedSerial,
          itemName: data.item?.name || '-',
          partNumber: data.item?.part_number || '-',
          brand: data.item?.brand?.name || '-',
          received: true,
          isExtra: true,
          lookupFound: true,
          itemDescription: data.item?.description || '-',
        },
      ]);

      setExtraSerialInput('');
      setFallbackSerial('');
      resetManualAdditionalItemFields();
      toast.success('Extra item added successfully.');
    } catch {
      setFallbackSerial(normalizedSerial);
      resetManualAdditionalItemFields();
      toast.error('Serial not found. Search and select an item below to include it as a manual additional item.');
    }
  }

  function handleAddFallbackItem() {
    if (!fallbackSerial) {
      toast.error('There is no pending serial number to add manually.');
      return;
    }

    if (!manualAdditionalSelectedItem?.id) {
      toast.error('Select an item first.');
      return;
    }

    setAdditionalItems((current) => [
      ...current,
      {
        quantity: 1,
        serial_number: fallbackSerial,
        item_id: manualAdditionalSelectedItem.id,
        part_number: manualAdditionalSelectedItem.part_number || '-',
        description: manualAdditionalSelectedItem.description || '-',
      },
    ]);

    setExtraSerialInput('');
    setFallbackSerial('');
    resetManualAdditionalItemFields();
    toast.success(`Serial number ${fallbackSerial} added as manual additional item.`);
  }

  function removeAdditionalItem(index: number) {
    setAdditionalItems((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  function validateSubmit() {
    if (!rma) {
      toast.error('RMA information is missing.');
      return false;
    }

    if (!trackingCode.trim()) {
      toast.error('Tracking code is required.');
      return false;
    }

    if (!carrierId) {
      toast.error('Carrier is required.');
      return false;
    }

    if (evidences.length === 0) {
      toast.error('At least one evidence is required (upload or camera photo).');
      return false;
    }

    if (!hasReceivedItems && !hasAdditionalItems) {
      toast.error('Mark at least one serial number as received or add a manual additional item.');
      return false;
    }

    return true;
  }

  async function handleSave() {
    if (!rma || !validateSubmit()) return;

    const receivingItemsPayload: ReceivingPayloadItem[] = receivingRows.map((row) => ({
      description: `${row.serialNumber} - ${row.itemName} - ${row.partNumber} - ${row.brand}`,
      quantityToReceive: 1,
      quantityAlreadyReceived: 0,
      quantity: row.received ? 1 : 0,
      isPhysicalItem: true,
      additionalItem: row.isExtra,
    }));

    const payload: ReceivingPayload = {
      trackingCode: trackingCode.trim(),
      type: 'RMA',
      return_id: rma.id,
      supplierId: null,
      carrierId,
      notes: notes.trim() || null,
      receivingItems: receivingItemsPayload,
      additionalItems: additionalItems.map(({ quantity, serial_number, item_id }) => ({ quantity, serial_number, item_id })),
    };

    const formData = new FormData();
    evidences.forEach((file) => {
      formData.append('pictures', file);
    });
    formData.append('data', JSON.stringify(payload));

    setSubmitting(true);
    try {
      await api.post('new-receiving', formData);
      toast.success('RMA receiving created successfully.');
      history.push('/new-receiving');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Could not create RMA receiving.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground xl:pl-60">
      <main className="px-4 pb-6 pt-20 lg:px-6 lg:pb-8">
        <section className="rounded-lg border border-border/70 bg-background/95 p-4 shadow-sm lg:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-xl font-semibold tracking-tight">New Receiving - RMA</h1>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => history.push('/new-receiving')}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading || submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>

          {loading ? <p className="text-sm text-muted-foreground">Loading RMA...</p> : null}

          <Card className="mb-4 p-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-1">
                <Label htmlFor="rma-number">RMA Number</Label>
                <Input id="rma-number" value={rma?.id || ''} disabled />
              </div>

              <div className="space-y-1">
                <Label htmlFor="tracking-code">Tracking Code *</Label>
                <Input id="tracking-code" value={trackingCode} disabled />
              </div>

              <div className="space-y-1">
                <Label htmlFor="carrier">Carrier *</Label>
                <Combobox id="carrier" value={carrierId} options={CARRIER_OPTIONS} onChange={setCarrierId} placeholder="Select carrier" disabled />
              </div>

              <div className="space-y-1">
                <Label htmlFor="customer">Customer</Label>
                <Input id="customer" value={rma?.order?.customer?.name || ''} disabled />
              </div>
            </div>
          </Card>

          <Card className="mb-4 p-4">
            <h2 className="mb-3 text-base font-semibold">RMA Details</h2>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-md border border-border/70 p-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Order #</p>
                <p className="text-sm font-medium">{rma?.order_num || '-'}</p>
              </div>
              <div className="rounded-md border border-border/70 p-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">RMA Status</p>
                <p className="text-sm font-medium">{rma?.status || '-'}</p>
              </div>
            </div>
          </Card>

          <Card className="mb-4 p-4">
            <h2 className="mb-3 text-base font-semibold">Returned Items</h2>
            <div className="mb-4 flex flex-wrap gap-2">
              <Button variant="outline" onClick={loadOrderItems} disabled={!rma || orderItemsLoading}>
                {orderItemsLoading ? 'Loading...' : 'Show serial numbers shipped in this order'}
              </Button>
            </div>
            <Table className="rounded-xl border border-border/70 bg-background shadow-sm [&_table]:min-w-full">
              <TableHeader className="bg-muted/35">
                <TableRow>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Received</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Serial Number</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Item Name</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Part Number</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Brand</TableHead>
                  <TableHead className="h-9 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Item Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receivingRows.length === 0 ? (
                  <TableRow>
                    <TableCell className="px-2 py-4 text-center text-sm text-muted-foreground" colSpan={6}>
                      No serial numbers in this RMA.
                    </TableCell>
                  </TableRow>
                ) : null}

                {receivingRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="px-2 py-1.5">
                      <label
                        className={`inline-flex min-h-11 min-w-[132px] cursor-pointer select-none items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                          row.received
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            : 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={row.received}
                          onChange={(event) => {
                            setReceivedState(row.id, event.target.checked);
                          }}
                        />
                        <span aria-hidden="true" className={`h-3 w-3 rounded-full ${row.received ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        {row.received ? 'Received' : 'Not Received'}
                      </label>
                    </TableCell>
                    <TableCell className="px-2 py-1.5 text-sm">{row.serialNumber}</TableCell>
                    <TableCell className="px-2 py-1.5 text-sm">{row.itemName}</TableCell>
                    <TableCell className="px-2 py-1.5 text-sm">{row.partNumber}</TableCell>
                    <TableCell className="px-2 py-1.5 text-sm">{row.brand}</TableCell>
                    <TableCell className="px-2 py-1.5 text-xs">{row.itemDescription}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card className="mb-4 p-4">
            <h2 className="mb-3 text-base font-semibold">Additional Items</h2>

            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[220px] flex-1 space-y-1">
                <Label htmlFor="extra-serial">Serial Number</Label>
                <Input
                  id="extra-serial"
                  value={extraSerialInput}
                  placeholder="Enter serial number"
                  onChange={(event) => setExtraSerialInput(event.target.value.toUpperCase())}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddExtraItem();
                    }
                  }}
                />
              </div>

              <Button className="shrink-0" onClick={handleAddExtraItem}>
                Add Item
              </Button>
            </div>

            {fallbackSerial ? (
              <div className="mt-4 rounded-md border border-border/70 bg-muted/10 p-3">
                <p className="mb-3 text-sm">
                  Serial number <span className="font-semibold">{fallbackSerial}</span> was not found. Search and select an item to include it as an additional
                  manual item.
                </p>

                <div className="space-y-1">
                  <Label htmlFor="manual-item-search">Item</Label>
                  <Input
                    id="manual-item-search"
                    value={manualAdditionalItemSearch}
                    placeholder="Type item name"
                    onChange={(event) => handleManualItemSearchChange(event.target.value)}
                  />

                  {manualAdditionalItemLoading ? <p className="text-xs text-muted-foreground">Searching items...</p> : null}
                  {!manualAdditionalItemLoading && manualAdditionalItemSearch.trim() && manualAdditionalItemOptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {manualAdditionalSelectedItem ? `Part Number: ${manualAdditionalSelectedItem.part_number || '-'}` : 'No items found.'}
                    </p>
                  ) : null}

                  {manualAdditionalItemOptions.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto rounded-md border border-border/70 bg-background">
                      {manualAdditionalItemOptions.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="block w-full border-b border-border/50 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50"
                          onClick={() => handleSelectManualItem(item)}
                        >
                          <span className="font-medium">{item.name}</span>
                          {/* <span className="ml-2 text-xs text-muted-foreground">(ID: {item.id})</span> */}
                          {item.description ? <p className="text-xs text-muted-foreground">{item.description}</p> : null}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {/* {manualAdditionalSelectedItem ? (
                    <p className="text-xs text-muted-foreground">
                      Selected: {manualAdditionalSelectedItem.name} (ID: {manualAdditionalSelectedItem.id})
                    </p>
                  ) : null} */}
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <Button onClick={handleAddFallbackItem}>Add Manual Item</Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFallbackSerial('');
                      resetManualAdditionalItemFields();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}

            {additionalItems.length > 0 ? (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium">Manual Additional Items</p>
                {additionalItems.map((item, index) => (
                  <div
                    key={`manual-additional-item-${index + 1}`}
                    className="flex items-center justify-between gap-2 rounded-md border border-border/70 p-3 text-sm"
                  >
                    <span className="truncate pr-2">
                      <strong>SN:</strong> {item.serial_number} <br /> <strong>Part Number:</strong> {item.part_number || '-'} <br />{' '}
                      <strong>Description:</strong> {item.description || '-'}
                      {/* <span className="text-muted-foreground">(Qty: 1)</span> */}
                    </span>
                    <Button variant="outline" className="h-8 px-2" onClick={() => removeAdditionalItem(index)}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}
          </Card>

          <Card className="mb-4 p-4">
            <h2 className="mb-3 text-base font-semibold">Evidence *</h2>
            <div className="mb-3 flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  uploadInputRef.current?.click();
                }}
              >
                Upload Files
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setCameraOpen(true);
                }}
              >
                Take Photo
              </Button>
            </div>

            <input ref={uploadInputRef} type="file" className="hidden" multiple onChange={handleEvidenceAdd} />

            <div className="space-y-2">
              {evidences.length === 0 ? <p className="text-sm text-muted-foreground">No evidence attached yet.</p> : null}
              {evidences.map((file, index) => (
                <div key={`${file.name}-${index + 1}`} className="flex items-center justify-between rounded-md border border-border/70 px-3 py-2 text-sm">
                  <span className="truncate pr-2">{file.name}</span>
                  <Button
                    variant="ghost"
                    className="h-8 px-2"
                    onClick={() => {
                      setEvidences((current) => current.filter((_, currentIndex) => currentIndex !== index));
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-4">
            <Label htmlFor="notes" className="mb-1 block">
              Notes (optional)
            </Label>
            <Input id="notes" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Add notes" />
          </Card>

          {/* Order Items Selection Modal */}
          {orderItemsModalOpen ? (
            <div
              className="fixed inset-0 z-[1000] bg-black/50"
              role="button"
              tabIndex={0}
              onMouseDown={handleCloseOrderItemsModal}
              onKeyDown={(event) => {
                if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
                  handleCloseOrderItemsModal();
                }
              }}
            >
              <div className="absolute inset-x-4 bottom-4 top-4 mx-auto max-w-2xl">
                <Card
                  className="flex h-full w-full flex-col overflow-hidden"
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <div className="shrink-0 border-b border-border/70 bg-background/95 p-4">
                    <h2 className="text-lg font-semibold">Select Serial Numbers from Order</h2>
                    <p className="text-sm text-muted-foreground">Choose which items from the order were received</p>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {orderItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No items found in this order.</p>
                    ) : (
                      <div className="space-y-2">
                        {orderItems.map((item) => {
                          const hasSerialNumber = Boolean(item.serial_number?.trim());

                          return (
                            <label
                              key={item.id}
                              className={`flex items-start gap-3 rounded-md border border-border/70 p-3 transition-colors ${
                                hasSerialNumber ? 'cursor-pointer hover:bg-muted/50' : 'cursor-not-allowed opacity-60'
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={selectedOrderItemIds.has(item.id)}
                                disabled={!hasSerialNumber}
                                onChange={(event) => {
                                  handleOrderItemSelect(item.id, event.target.checked);
                                }}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold">{item.serial_number?.trim() || 'No serial number'}</div>
                                <div className="mt-1 text-xs text-muted-foreground">
                                  <p>Item: {item.item?.name || '-'}</p>
                                  <p>Part #: {item.item?.part_number || '-'}</p>
                                  <p>Condition: {item.condition || '-'}</p>
                                  {!hasSerialNumber ? <p className="font-medium text-red-600">This item cannot be selected.</p> : null}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 border-t border-border/70 bg-background/95 p-4">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={handleCloseOrderItemsModal}>
                        Cancel
                      </Button>
                      <Button onClick={handleApplyOrderItems} disabled={selectedOrderItemIds.size === 0}>
                        Apply Selected ({selectedOrderItemIds.size})
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          ) : null}

          <CameraCapture
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCapture={(photo) => {
              setEvidences((current) => [...current, photo]);
            }}
          />
        </section>
      </main>
    </div>
  );
}
